import { AddCircle, CloseRounded, Delete } from '@mui/icons-material'
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import Grid from '@mui/material/Unstable_Grid2'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  deleteResourcecategory,
  storeResourcecategory,
  toggleFormResourcecategories,
  updateResourcecategory
} from '../../../../../redux/actions/resourcecategories'
import { formValidator, resetFormValidator } from '../../../../../redux/actions/validator'
import { IRootState } from '../../../../../redux/types'
import { StyledTabs } from '../../../../shared/components/StyledTabs'
import TabPanel from '../../../../shared/components/TabPanel'
import {
  BrandMultiAutocomplete,
  CancelButton,
  DeleteButton,
  ResourcetypeSelect,
  RoleAutocomplete,
  SaveButton
} from '../../../../shared/components/inputs'
import InputCountry from '../../../../shared/components/inputs/InputCountry'
import useTabState from '../../../../shared/hooks/useTabState'
import { StyledDialog } from '../../../../shared/styles/StyledDialog'
import RoleInterface from '../../../users/models/RoleInterface'
import { CustomTab } from '../../../users/ui/components/modals/create/CustomTab'

interface IResourcecategory {
  id: number
  name: string
  resourcetypeId: number
  countries: any
  brands: any
  role: null | RoleInterface
  translations: any
}

function CategoriesModal({ resourcetypeId = 1 }: { resourcetypeId: number }) {
  const { t } = useTranslation(['global'])

  const dispatch = useDispatch()
  const { user: authUser } = useSelector((state: IRootState) => state.auth)
  const { resourcecategorySelected, showForm } = useSelector((state: IRootState) => state.resourcecategory)
  const { isValidForm, errorTab, msgForm } = useSelector((state: IRootState) => state.validator)

  const [resourcecategory, setResourcecategory] = useState<IResourcecategory>({
    id: 0,
    name: 'default',
    resourcetypeId: resourcetypeId,
    countries: [],
    brands: [],
    role: null,
    translations: [{ language_id: 1, text: '' }]
  })

  const { currentTab, setCurrentTab, a11yProps } = useTabState()

  useEffect(() => {
    if (isValidForm) {
      dispatch(resetFormValidator())

      if (resourcecategory.id === 0) dispatch(storeResourcecategory(resourcecategory))
      else dispatch(updateResourcecategory(resourcecategory))
    }
  }, [isValidForm])

  useEffect(() => {
    if (resourcecategorySelected !== null && resourcecategorySelected.id !== resourcecategory.id) {
      setResourcecategory({
        id: resourcecategorySelected.id,
        name: resourcecategorySelected.name,
        resourcetypeId: resourcecategorySelected.resourcetype_id,
        brands: resourcecategorySelected.brands,
        countries: resourcecategorySelected.countries,
        role: resourcecategorySelected.role,
        translations: resourcecategorySelected.translations
      })
    } else {
      setResourcecategory({
        id: 0,
        name: 'default',
        resourcetypeId: resourcetypeId,
        countries: [],
        brands: [],
        role: null,
        translations: [{ language_id: 1, text: '' }]
      })
    }
  }, [resourcecategorySelected])

  const handleInputChange = (inputName: string, value: any) => {
    setResourcecategory({ ...resourcecategory, [inputName]: value })
  }

  const handleSubmit = () => {
    dispatch(
      formValidator({
        name: [resourcecategory.name, 'required'],
        resourcetypeId: [resourcecategory.resourcetypeId, 'required'],
        role: [resourcecategory.role, 'required'],
        countries: [resourcecategory.countries, 'notempty'],
        translations: [resourcecategory.translations, 'notempty']
      })
    )
  }

  const addTranslation = () => {
    const newTranslation = { language_id: 1, text: '' }
    let stateValue = resourcecategory.translations
    stateValue.push(newTranslation)

    setResourcecategory({ ...resourcecategory, translations: stateValue })
  }

  const deleteTranslation = (key: number) => {
    let stateValue = [...resourcecategory.translations]
    stateValue.splice(key, 1)

    setResourcecategory({ ...resourcecategory, translations: stateValue })
  }

  const handleChangeTranslations = (nameInput: string, key: number) => (event: any) => {
    let stateValue = resourcecategory.translations
    stateValue[key][nameInput] = event.target.value

    setResourcecategory({
      ...resourcecategory,
      translations: stateValue
    })
  }

  return (
    <StyledDialog
      open={showForm}
      onClose={() => dispatch(toggleFormResourcecategories())}
      aria-describedby="alert-dialog-description"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="alert-dialog-title">
        <Grid container justifyContent="space-between">
          <Typography variant="h6">
            {t('admin.library.create-category')} {msgForm.id}
          </Typography>
          <Button onClick={() => dispatch(toggleFormResourcecategories())} color="primary" variant="text">
            <CloseRounded />
          </Button>
        </Grid>
      </DialogTitle>

      <DialogContent sx={{ pb: 3, pt: 2 }}>
        <Grid container alignItems="flex-start" justifyContent="space-between" spacing={2}>
          <Grid xs={12} md={6}>
            <FormControl fullWidth variant="filled">
              <ResourcetypeSelect value={resourcecategory.resourcetypeId} handleInputChange={handleInputChange} />
            </FormControl>
          </Grid>

          <Grid xs={12} md={6}>
            <FormControl fullWidth variant="filled">
              <InputCountry value={resourcecategory.countries} handleInputChange={handleInputChange} />
            </FormControl>
          </Grid>

          <Grid xs={12} md={8}>
            <FormControl fullWidth variant="filled">
              <BrandMultiAutocomplete value={resourcecategory.brands} handleInputChange={handleInputChange} />
            </FormControl>
          </Grid>
          <Grid xs={12} md={4}>
            <FormControl fullWidth variant="filled">
              <RoleAutocomplete value={resourcecategory.role} handleInputChange={handleInputChange} />
            </FormControl>
          </Grid>
        </Grid>

        <Grid xs={12}>
          <StyledTabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} aria-label="disabled tabs example">
            <CustomTab error={errorTab.general} label={`${t('general')}-${t('language')}`} {...a11yProps(0)} />
          </StyledTabs>
        </Grid>
        <TabPanel value={currentTab} index={0}>
          {resourcecategory.translations.map((translation: any, index: number) => (
            <Grid container spacing={2} key={translation.text}>
              <Grid xs={12} md={5}>
                <TextField
                  id={`txt-text${index}`}
                  label={t('name')}
                  type="text"
                  variant="filled"
                  value={translation.text}
                  autoFocus={true}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChangeTranslations('text', index)}
                  fullWidth
                />
              </Grid>
              <Grid xs={12} md={5}>
                <FormControl fullWidth variant="filled">
                  <InputLabel shrink={true} id="languages-label">
                    {`${t('languages')}*`}
                  </InputLabel>
                  <Select
                    value={translation.language_id}
                    labelId="languages-label"
                    id={`languages-select-outlined${index}`}
                    fullWidth
                    label={t('languages')}
                    onChange={handleChangeTranslations('language_id', index)}
                  >
                    {authUser.languages_name.map((language: any) => (
                      <MenuItem key={language.id} value={language.id}>
                        {language.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid xs={6} md={2}>
                <IconButton aria-label="add" color="primary" size="medium" onClick={() => addTranslation()}>
                  <AddCircle />
                </IconButton>
                {index !== 0 && (
                  <IconButton
                    aria-label="delete"
                    color="primary"
                    size="medium"
                    onClick={() => deleteTranslation(resourcecategory.translations.index)}
                  >
                    <Delete />
                  </IconButton>
                )}
              </Grid>
            </Grid>
          ))}
        </TabPanel>
      </DialogContent>

      <DialogActions sx={[!isValidForm && Object.keys(msgForm).length > 0 && { bgcolor: 'warning.light' }]}>
        {resourcecategory.id !== 0 && (
          <DeleteButton onClick={() => dispatch(deleteResourcecategory(resourcecategory.id))}>{t('delete')}</DeleteButton>
        )}
        <CancelButton onClick={() => dispatch(toggleFormResourcecategories())}>{t('cancel')}</CancelButton>
        <SaveButton onClick={handleSubmit}>{t('save')}</SaveButton>
      </DialogActions>
    </StyledDialog>
  )
}

export default CategoriesModal
